import * as React from 'react';
import { WaitingListItem, WaitingListPriority } from '../../../models';
import { AutocompleteTextField, TextInputField } from '../fields';
import { ComboField } from '../fields/ComboField';
import { DateField } from '../fields/DateField';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useSelector } from 'react-redux';
import { useGeneralSettings, useServicesSettings } from '../../../store/settings/hooks';
import { serviceId, serviceName } from '../../../shared/appointmint/appointment';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { ClientEntity, ProfessionalEntity } from '../../../store/entities/state';
import { ApplicationState } from '../../../store';
import {
    createClientSearchConfig,
} from '../../../shared/clientSearch';

const getProfessionals = getEntitiesDataItems<ProfessionalEntity>('professionals');
const getClients = getEntitiesDataItems<ClientEntity>('clients');

export interface WaitingListItemFormProps {
    value: Partial<WaitingListItem>;
    onChange: (value: Partial<WaitingListItem>) => void;
}

const WaitingListItemForm: React.FC<WaitingListItemFormProps> = ({ value, onChange }) => {
    const t = useTranslations();
    const generalSettings = useGeneralSettings();
    const servicesSettings = useServicesSettings();
    const professionals = useSelector((state: ApplicationState) => getProfessionals(state));
    const clients = useSelector((state: ApplicationState) => getClients(state));
    const clientSearchConfig = React.useMemo(() => createClientSearchConfig(clients), [clients]);

    const selectedProfessional = professionals.find(p => p.id === value.professionalId);
    const servicesForProfessional = selectedProfessional?.capabilities ?? Object.keys(servicesSettings);
    const professionalsForService = value.service
        ? professionals.filter(p => p.capabilities?.includes(value.service!))
        : professionals;

    const onClientChanged = (option: string) => {
        const client = clientSearchConfig.getClientByOption(option);
        onChange({
            ...value,
            clientId: client?.id ?? '',
            clientName: (client?.name ?? option) || undefined,
        });
    };

    const onProfessionalChanged = (name: string) => {
        const professional = professionals.find(p => p.name === name);
        const capabilities = professional?.capabilities ?? [];
        const shouldPreserveService = !professional || !value.service || capabilities.includes(value.service);
        onChange({
            ...value,
            professionalId: professional?.id,
            professionalName: name || undefined,
            service: shouldPreserveService ? value.service : undefined,
        });
    };

    const onServiceChanged = (name: string) => {
        const type = serviceId(name, generalSettings);
        const professionalStillValid = !type || professionals.some(p => p.capabilities?.includes(type) && p.id === value.professionalId);
        onChange({
            ...value,
            service: type || undefined,
            ...(!professionalStillValid && { professionalId: undefined, professionalName: undefined }),
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            <AutocompleteTextField
                id="waiting-list-client"
                label={t(TK.client)}
                value={clientSearchConfig.getOptionByClientId(value.clientId) ?? value.clientName ?? ''}
                options={clientSearchConfig.options}
                optionLabelResolver={clientSearchConfig.optionLabelResolver}
                searchTextResolver={clientSearchConfig.searchTextResolver}
                searchMatcher={clientSearchConfig.searchMatcher}
                onValueChanged={onClientChanged}
            />
            <AutocompleteTextField
                id="waiting-list-service"
                label={t(TK.service)}
                value={serviceName(value.service ?? '', generalSettings)}
                options={servicesForProfessional.map(s => serviceName(s, generalSettings))}
                onValueChanged={onServiceChanged}
            />
            <AutocompleteTextField
                id="waiting-list-professional"
                label={`${t(TK.professional)} (${t(TK.optional).toLowerCase()})`}
                value={value.professionalName ?? ''}
                options={professionalsForService.map(p => p.name)}
                onValueChanged={onProfessionalChanged}
            />
            <TextInputField
                id="waiting-list-notes"
                label={t(TK.notes)}
                value={value.notes ?? ''}
                type="textarea"
                onChange={notes => onChange({ ...value, notes })}
            />
            <ComboField
                id="waiting-list-priority"
                label={`${t(TK.priority)} (${t(TK.optional).toLowerCase()})`}
                value={value.priority !== undefined ? String(value.priority) : String(WaitingListPriority.Normal)}
                options={[
                    { value: String(WaitingListPriority.Normal), label: t(TK.priorityNormal) },
                    { value: String(WaitingListPriority.High), label: t(TK.priorityHigh) },
                    { value: String(WaitingListPriority.Urgent), label: t(TK.priorityUrgent) },
                ]}
                onChange={v => onChange({ ...value, priority: v !== undefined ? Number(v) : WaitingListPriority.Normal })}
            />
            <DateField
                id="waiting-list-limit-date"
                label={`${t(TK.limitDate)} (${t(TK.optional).toLowerCase()})`}
                value={value.limitDate ?? ''}
                onChange={limitDate => onChange({ ...value, limitDate: limitDate || undefined })}
            />
        </div>
    );
};

export default WaitingListItemForm;
